import { type UpsellsApiExperiment } from "@tablescape/state";

export type UpsellsExperimentInput = Pick<
  UpsellsApiExperiment,
  "id" | "variant"
>;

const UPSELL_EXPERIMENT_STORAGE_KEY = "upsells-api-experiment";

export const DEFAULT_UPSELL_EXPERIMENT_ID = "default";

export function isActiveUpsellExperiment(
  experiment: UpsellsApiExperiment
): boolean {
  return experiment.id !== DEFAULT_UPSELL_EXPERIMENT_ID;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

const coerceExperimentVariant = (variant: unknown): number | null => {
  if (typeof variant === "number" && Number.isFinite(variant)) {
    return variant;
  }

  if (typeof variant === "string" && variant.trim() !== "") {
    const parsed = Number(variant);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

/** Normalize API / storage shapes to the contract numeric variant field. */
export function normalizeUpsellsApiExperiment(
  value: unknown
): UpsellsApiExperiment | null {
  if (!isRecord(value)) {
    return null;
  }

  const { id, name } = value;
  const variant = coerceExperimentVariant(value.variant);

  if (typeof id === "string" && typeof name === "string" && variant !== null) {
    return { id, name, variant };
  }

  return null;
}

export function readUpsellExperiment(): UpsellsApiExperiment | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(UPSELL_EXPERIMENT_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);

    return normalizeUpsellsApiExperiment(parsed);
  } catch {
    return null;
  }
}

export function readUpsellExperimentInput(): UpsellsExperimentInput | null {
  const experiment = readUpsellExperiment();

  return experiment ? { id: experiment.id, variant: experiment.variant } : null;
}

export function writeUpsellExperiment(experiment: unknown): void {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = normalizeUpsellsApiExperiment(experiment);

  if (!normalized) {
    return;
  }

  try {
    window.localStorage.setItem(
      UPSELL_EXPERIMENT_STORAGE_KEY,
      JSON.stringify(normalized)
    );
  } catch {
    // localStorage unavailable — experiment not persisted for next request
  }
}
